<template>
  <div class="npc-templates-list">
    <div class="templates-list-header">
      <h3 class="gm-section-title">NPC Templates</h3>
      <input
          v-model="searchQuery"
          class="gm-input search-input"
          placeholder="Search templates"
          type="text"
          aria-label="Search templates"
      />
      <select v-model="selectedBehavior" class="gm-select behavior-filter" aria-label="Filter by behaviour">
        <option value="">All behaviours</option>
        <option v-for="behavior in behaviors" :key="behavior" :value="behavior">
          {{ formatBehavior(behavior) }}
        </option>
      </select>
      <button class="gm-btn gm-btn--sm gm-btn--ghost refresh-btn" title="Reload the template list" @click="refreshTemplates">
        <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><path d="M13 8a5 5 0 11-1.6-3.7M13 2.6V5h-2.4"/></svg>
      </button>
    </div>

    <div ref="templatesContainer" class="templates-container gm-scroll">
      <div v-if="isLoading" class="gm-empty loading-indicator">
        <div class="gm-empty__title">Loading Templates</div>
      </div>

      <div v-else-if="filteredTemplates.length === 0" class="gm-empty no-templates">
        <div class="gm-empty__title">No Templates Found</div>
        <div class="gm-empty__hint">Try a different search or behavior filter.</div>
      </div>

      <div
          v-for="template in filteredTemplates"
          v-else
          :key="template.id"
          class="template-cell"
          draggable="true"
          @click="selectTemplate(template)"
          @dragstart="onDragStart($event, template)"
      >
        <div class="template-icon">
          <img
              v-if="template.avatar"
              :alt="template.name"
              :src="template.avatar"
              class="template-image"
              @error="handleImageError($event, template)"
          />
          <div v-else class="template-placeholder fancy-placeholder">
            <span class="initials">{{ getTemplateInitials(template.name) }}</span>
          </div>
        </div>
        <div class="template-info">
          <div class="template-name">{{ template.name }}</div>
          <div v-if="template.behavior" class="gm-chip gm-chip--muted template-behavior">{{ template.behavior }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {characterTemplatesService} from '@/services/CharacterTemplatesService.ts';
import {dragDropService} from '@/services/DragDropService.js';

export default {
  name: 'NPCTemplatesList',
  props: {
    // Optional title for the component
    title: {
      type: String,
      default: 'NPC Templates'
    }
  },
  data() {
    return {
      templates: [],
      isLoading: true,
      searchQuery: '',
      selectedBehavior: '',
      error: null,
      behaviors: [] // Will be populated from templates
    };
  },
  computed: {
    filteredTemplates() {
      let result = this.templates;

      // Filter by behavior if selected
      if (this.selectedBehavior) {
        result = result.filter(template => template.behavior === this.selectedBehavior);
      }

      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        result = result.filter(template =>
            template.name.toLowerCase().includes(query) ||
            (template.description && template.description.toLowerCase().includes(query))
        );
      }

      return result;
    }
  },
  async mounted() {
    try {
      // Register event listeners
      characterTemplatesService.on('loadingStarted', this.onLoadingStarted);
      characterTemplatesService.on('templatesLoaded', this.onTemplatesLoaded);
      characterTemplatesService.on('loadingFailed', this.onLoadingFailed);

      // Initialize the service and load templates
      await characterTemplatesService.initialize();
    } catch (error) {
      console.error('Error initializing templates list:', error);
      this.error = error.message || 'Failed to load templates';
      this.isLoading = false;
    }
  },
  beforeUnmount() {
    // Clean up event listeners
    characterTemplatesService.off('loadingStarted', this.onLoadingStarted);
    characterTemplatesService.off('templatesLoaded', this.onTemplatesLoaded);
    characterTemplatesService.off('loadingFailed', this.onLoadingFailed);
  },
  methods: {
    // Event handlers
    onLoadingStarted() {
      this.isLoading = true;
      this.error = null;
    },

    onTemplatesLoaded(templates) {
      this.templates = templates;
      this.isLoading = false;

      // Extract unique behaviors for the filter
      const behaviorSet = new Set();
      templates.forEach(template => {
        if (template.behavior) {
          behaviorSet.add(template.behavior);
        }
      });
      this.behaviors = Array.from(behaviorSet);
    },

    onLoadingFailed(error) {
      console.error('Failed to load templates:', error);
      this.error = error.message || 'Failed to load templates';
      this.isLoading = false;
    },

    // UI actions
    async refreshTemplates() {
      try {
        this.isLoading = true;
        await characterTemplatesService.refresh();
      } catch (error) {
        console.error('Error refreshing templates:', error);
      }
    },

    selectTemplate(template) {
      // Emit the selected template to parent components
      this.$emit('template-selected', template);
    },

    // Helper methods
    getTemplateInitials(name) {
      if (!name) return '??';

      // Get first two characters of the name
      return name.substring(0, 2).toUpperCase();
    },

    // Handle image load errors
    handleImageError(event, template) {
      // Replace the image with a fancy placeholder
      const img = event.target;
      const parent = img.parentNode;

      // Create fancy placeholder
      const placeholder = document.createElement('div');
      placeholder.className = 'template-placeholder fancy-placeholder';

      // Add initials
      const initials = document.createElement('span');
      initials.className = 'initials';
      initials.textContent = this.getTemplateInitials(template.name);
      placeholder.appendChild(initials);

      // Replace the image with the placeholder
      parent.replaceChild(placeholder, img);
    },

    formatBehavior(behavior) {
      if (!behavior) return '';

      // Convert camelCase or snake_case to Title Case with spaces
      return behavior
          // Insert a space before all uppercase letters
          .replace(/([A-Z])/g, ' $1')
          // Replace underscores with spaces
          .replace(/_/g, ' ')
          // Capitalize first letter
          .replace(/^./, str => str.toUpperCase())
          // Trim any extra spaces
          .trim();
    },

    // Handle drag start event
    onDragStart(event, template) {
      console.log('Drag start:', template);

      // Set the drag data to the template's JSON string
      const templateJson = JSON.stringify(template);
      event.dataTransfer.setData('text/plain', templateJson);
      // Also set it as application/json for better compatibility
      event.dataTransfer.setData('application/json', templateJson);
      // Set a custom type to identify this as an NPC template
      event.dataTransfer.setData('application/npc-template', templateJson);
      console.log('Set drag data:', templateJson);

      // Set the drag effect to 'copy' to indicate that we're copying the template
      event.dataTransfer.effectAllowed = 'copy';

      // Add a class to the dragged element for visual feedback
      event.target.classList.add('dragging');

      // Create a custom drag image
      const dragImage = this.createDragImage(template);
      if (dragImage) {
        // Use the custom drag image
        event.dataTransfer.setDragImage(dragImage, dragImage.width / 2, dragImage.height / 2);

        // Clean up the drag image after a short delay
        setTimeout(() => {
          if (dragImage.parentNode) {
            document.body.removeChild(dragImage);
          }
        }, 100);
      }

      // Notify the drag drop service that we've started dragging
      dragDropService.startDrag({
        type: 'npc-template',
        data: template
      });

      // Remove the class when the drag ends
      const onDragEnd = () => {
        event.target.classList.remove('dragging');
        event.target.removeEventListener('dragend', onDragEnd);
      };

      event.target.addEventListener('dragend', onDragEnd);
    },

    // Create a custom drag image
    createDragImage(template) {
      // Create a new element for the drag image
      const dragImage = document.createElement('div');
      dragImage.className = 'custom-drag-image';

      // Add template icon
      const iconContainer = document.createElement('div');
      iconContainer.className = 'drag-image-icon';

      if (template.avatar) {
        const img = document.createElement('img');
        img.src = template.avatar;
        img.alt = template.name;

        // Handle image load errors
        img.onerror = () => {
          // Create fancy placeholder instead
          iconContainer.classList.add('fancy-placeholder');
          iconContainer.innerHTML = '';

          const initials = document.createElement('span');
          initials.className = 'initials';
          initials.textContent = this.getTemplateInitials(template.name);
          iconContainer.appendChild(initials);
        };

        iconContainer.appendChild(img);
      } else {
        // Create fancy placeholder
        iconContainer.classList.add('fancy-placeholder');

        const initials = document.createElement('span');
        initials.className = 'initials';
        initials.textContent = this.getTemplateInitials(template.name);
        iconContainer.appendChild(initials);
      }

      // Add template name
      const nameContainer = document.createElement('div');
      nameContainer.className = 'drag-image-name';
      nameContainer.textContent = template.name;

      // Add template behavior if available
      if (template.behavior) {
        const behaviorContainer = document.createElement('div');
        behaviorContainer.className = 'drag-image-behavior';
        behaviorContainer.textContent = template.behavior;
        dragImage.appendChild(behaviorContainer);
      }

      // Assemble the drag image
      dragImage.appendChild(iconContainer);
      dragImage.appendChild(nameContainer);

      // Add styles
      Object.assign(dragImage.style, {
        position: 'absolute',
        top: '-1000px', // Position off-screen
        padding: '10px',
        background: '#181b1c',
        border: '1px solid rgba(255, 215, 0, 0.55)',
        borderRadius: '6px',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.55)',
        color: '#f3e7cf',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '5px',
        zIndex: '9999',
        pointerEvents: 'none'
      });

      Object.assign(iconContainer.style, {
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f1213',
        borderRadius: '4px',
        marginBottom: '5px',
        position: 'relative',
        overflow: 'hidden'
      });

      // Apply additional styles for fancy placeholder
      if (iconContainer.classList.contains('fancy-placeholder')) {
        Object.assign(iconContainer.style, {
          background: '#0f1213',
          border: '1px solid rgba(250, 218, 149, 0.14)',
          boxShadow: 'none'
        });

        // Style the initials
        if (iconContainer.querySelector('.initials')) {
          Object.assign(iconContainer.querySelector('.initials').style, {
            position: 'relative',
            zIndex: '2',
            fontSize: '1rem',
            fontWeight: 'bold',
            color: '#fada95'
          });
        }
      }

      if (iconContainer.querySelector('img')) {
        Object.assign(iconContainer.querySelector('img').style, {
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain'
        });
      }

      Object.assign(nameContainer.style, {
        fontWeight: 'bold',
        fontSize: '12px',
        textAlign: 'center',
        maxWidth: '100px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      });

      if (dragImage.querySelector('.drag-image-behavior')) {
        Object.assign(dragImage.querySelector('.drag-image-behavior').style, {
          fontSize: '10px',
          color: '#9a9078',
          textTransform: 'capitalize'
        });
      }

      // Add to the document
      document.body.appendChild(dragImage);

      return dragImage;
    }
  }
};
</script>

<style scoped>
.npc-templates-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--gm-surface-1);
  color: var(--gm-text);
  border-radius: var(--gm-radius);
  overflow: hidden;
}

.templates-list-header {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
  padding: var(--gm-gap-xs) var(--gm-gap-sm);
  background: var(--gm-surface-2);
  border-bottom: 1px solid var(--gm-border-dim);
}

.templates-list-header h3 {
  margin: 0;
}

.search-input,
.behavior-filter {
  min-width: 0;
  padding: 0.3rem 0.45rem;
  font-size: var(--gm-text-xs);
}

.search-input {
  flex: 1 1 55%;
}

.behavior-filter {
  flex: 1 1 45%;
}

.refresh-btn {
  padding: 0;
  width: 24px;
  height: 24px;
  flex: none;
}

.glyph {
  width: 12px;
  height: 12px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.templates-container {
  flex: 1;
  padding: var(--gm-gap-sm);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  grid-gap: var(--gm-gap-sm);
  align-content: start;
}

.loading-indicator, .no-templates {
  grid-column: 1 / -1;
}

.template-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.3rem;
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: background var(--gm-duration) var(--gm-ease),
              border-color var(--gm-duration) var(--gm-ease),
              transform var(--gm-duration) var(--gm-ease);
}

.template-cell:hover {
  background: var(--gm-accent-soft);
  border-color: var(--gm-border);
  transform: translateY(-2px);
}

.template-cell.dragging {
  opacity: 0.9;
  background: var(--gm-accent-soft);
  border: 1px solid var(--gm-border-hi);
  transform: scale(1.05);
  z-index: 1000;
  position: relative;
  cursor: grabbing;
}

.template-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--gm-gap-xs);
}

.template-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.template-placeholder {
  width: 100%;
  height: 100%;
  background: var(--gm-surface-sunken);
  color: var(--gm-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--gm-text-sm);
  border-radius: var(--gm-radius-sm);
}

.fancy-placeholder {
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  overflow: hidden;
  position: relative;
}

.fancy-placeholder::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 30%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent);
}

.fancy-placeholder .initials {
  position: relative;
  z-index: 2;
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-sm);
  font-weight: 700;
  color: var(--gm-accent);
}

.template-info {
  width: 100%;
  text-align: center;
}

.template-name {
  font-size: var(--gm-text-xs);
  line-height: var(--gm-leading-tight);
  color: var(--gm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.template-behavior {
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  letter-spacing: var(--gm-track-tight);
  color: var(--gm-text-dim);
  text-transform: uppercase;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .templates-container {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}

@media (max-width: 768px) {
  .templates-container {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  }

  .templates-list-header {
    flex-wrap: wrap;
  }
}
</style>